import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import {
  Wallet, ExternalLink, Scissors, ChevronRight,
  ShieldCheck, Pencil, CheckCircle2, Clock, Send, Sparkles,
  Upload, XCircle, Loader2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  useMyDataQuery,
  useMyPixVerificationQuery,
  useRequestCpmPayoutMutation,
  useSubmitMyPixVerificationMutation,
  useUpdateMyPayoutDataMutation,
  type CpmWallet,
  type SubmitMyPixVerificationMutation,
} from "@/generated/graphql";
import { usePixVerificationDocumentUpload } from "@/hooks/use-pix-verification-document-upload";
import { GET_MY_DATA, MY_CPM_WALLET } from "@/queries/queries";
import { CancelPendingHoldButton } from "@/components/wallet/CancelPendingHoldButton";
import { formatCents } from "@/lib/format/currency";
import { buildWhatsappPayoutUrl } from "@/lib/buildWhatsappPayoutUrl";
import { resolveSupportWhatsapp } from "@/lib/resolveSupportWhatsapp";
import { formatBankAccount } from "@/lib/payout/formatBankAccount";
import { isValidCpfOrCnpj } from "@/lib/payout/validateBrazilianDocument";
import { MIN_PAYOUT_CENTS, PAYOUT_FEE_PERCENT, calcPayoutFee, calcPayoutNet } from "@/lib/payout/constants";
import { buildPayoutRequestInput } from "@/lib/payout/buildPayoutRequestInput";
import { deriveWalletPendingState } from "@/lib/wallet/deriveWalletPendingState";
import { resolveWalletMobileActions } from "@/lib/wallet/resolveWalletMobileActions";
import { WalletStatement } from "@/components/wallet/WalletStatement";
import { MonthlyEarningsChart } from "@/components/wallet/MonthlyEarningsChart";

// Shared by the per-campaign link buttons and the total row's spacer.
const CAMPAIGN_LINK_WIDTH = "w-8 sm:w-32";
const ACCEPTED_DOCUMENT_TYPES = ["image/jpeg", "image/png", "application/pdf"];
const ACCOUNT_WITH_DIGIT_PATTERN = /^\d[\d.\s]*-[\dXx]$/;
const VERIFICATION_REFRESH_WARNING =
  "Documento enviado, mas não foi possível atualizar o status agora.";

// RG carries data on both sides; a CNH photo or a PDF usually needs only one
// file, so the back is optional.
type DocumentSide = "front" | "back";
type DocumentFiles = Record<DocumentSide, File | null>;
const NO_DOCUMENT_FILES: DocumentFiles = { front: null, back: null };

function DocumentSlot({
  id,
  label,
  hint,
  file,
  disabled,
  onChange,
}: {
  id: string;
  label: string;
  hint: string;
  file: File | null;
  disabled: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  return (
    <div className="space-y-1.5 min-w-0">
      <Label htmlFor={id} className="text-xs">{label}</Label>
      <label
        htmlFor={id}
        className={cn(
          "flex flex-col items-center justify-center gap-1.5 border border-dashed rounded-lg p-3 text-center cursor-pointer hover:border-muted-foreground/50 transition-colors min-h-[76px]",
          file ? "border-emerald-500/50 bg-emerald-500/5" : "border-border",
        )}
      >
        {file ? (
          <CheckCircle2 className="w-4 h-4 text-emerald-400" />
        ) : (
          <Upload className="w-4 h-4 text-muted-foreground" />
        )}
        <span className="text-xs text-muted-foreground break-words line-clamp-2 max-w-full">
          {file ? file.name : hint}
        </span>
        <input
          id={id}
          type="file"
          accept="image/jpeg,image/png,application/pdf"
          className="sr-only"
          disabled={disabled}
          onChange={onChange}
        />
      </label>
    </div>
  );
}

interface FormState {
  name: string;
  cpf: string;
  bank: string;
  agency: string;
  account: string;
  pixKey: string;
}

interface PayoutData {
  name: string;
  document: string;
  bank?: string | null;
  agency?: string | null;
  account?: string | null;
  pix_key?: string | null;
}

function payoutDataToForm(data: PayoutData): FormState {
  return {
    name: data.name,
    cpf: data.document,
    bank: data.bank ?? "",
    agency: data.agency ?? "",
    account: data.account ?? "",
    pixKey: data.pix_key ?? "",
  };
}

function hasAccountVerifierDigit(value: string): boolean {
  return ACCOUNT_WITH_DIGIT_PATTERN.test(value.trim());
}

function normalizeBrazilianDocument(value: string | null | undefined): string {
  return String(value ?? "").replace(/\D/g, "");
}

function pixVerificationErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : "";
  if (message.includes("pix_verification_cpf_in_use")) {
    return "Este CPF/CNPJ já está vinculado a outra conta.";
  }
  if (message.includes("pix_verification_already_pending")) {
    return "Seu documento já está em análise.";
  }
  if (message.includes("pix_verification_already_verified")) {
    return "Seu PIX já foi verificado.";
  }
  if (message.includes("pix_verification_invalid_document")) {
    return "Informe um CPF ou CNPJ válido.";
  }
  if (message.includes("pix_verification_document_required")) {
    return "Envie o comprovante do documento.";
  }
  return "Não foi possível enviar a verificação. Tente novamente.";
}

const EMPTY_FORM: FormState = {
  name: "",
  cpf: "",
  bank: "",
  agency: "",
  account: "",
  pixKey: "",
};

const EarnWallet = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: walletData, loading: walletLoading, refetch: refetchWallet } = useQuery<{ myCpmWallet: CpmWallet }>(
    MY_CPM_WALLET,
    { fetchPolicy: "cache-and-network" },
  );
  const { data: myDataResponse, loading: myDataLoading } = useMyDataQuery();
  const [updateMyPayoutData, { loading: savingPayoutData }] = useUpdateMyPayoutDataMutation({
    refetchQueries: [{ query: GET_MY_DATA }],
  });
  const [requestCpmPayout, { loading: requestingPayout }] = useRequestCpmPayoutMutation({
    refetchQueries: [{ query: MY_CPM_WALLET }],
    awaitRefetchQueries: true,
  });
  const {
    data: verificationData,
    loading: verificationLoading,
    error: verificationQueryError,
    refetch: refetchVerification,
  } = useMyPixVerificationQuery({ fetchPolicy: "cache-and-network" });
  const [submitVerification, { loading: submittingVerification }] =
    useSubmitMyPixVerificationMutation();
  const {
    uploadDocument,
    isLoading: uploadingDocument,
  } = usePixVerificationDocumentUpload();

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [statementRefresh, setStatementRefresh] = useState(0);
  const [savedForm, setSavedForm] = useState<FormState | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [documentFiles, setDocumentFiles] = useState<DocumentFiles>(NO_DOCUMENT_FILES);
  // The upload hook's flag drops between the front and back uploads; this one
  // spans both, so the send button cannot be pressed twice mid-submission.
  const [uploadingDocuments, setUploadingDocuments] = useState(false);
  const [documentError, setDocumentError] = useState<string | null>(null);
  const [verificationSubmitError, setVerificationSubmitError] = useState<string | null>(null);
  const [submittedVerification, setSubmittedVerification] = useState<
    SubmitMyPixVerificationMutation["submitMyPixVerification"] | null
  >(null);

  const loading = walletLoading || myDataLoading;
  const wallet: CpmWallet | undefined = walletData?.myCpmWallet;
  const me = myDataResponse?.myData;
  const isSaved = savedForm !== null;
  const {
    pendingPayoutCents,
    affiliatePendingCents,
    hasPendingPayout,
    hasAffiliateHold,
    isRedemptionHold,
  } = deriveWalletPendingState({
    pendingPayoutCents: wallet?.pending_payout_cents,
    affiliatePendingCents: wallet?.affiliate_pending_cents,
    pendingKind: wallet?.pending_kind,
  });
  const serverVerification = verificationData?.myPixVerification ?? null;
  const usingSubmittedVerification =
    submittedVerification !== null && serverVerification?.id !== submittedVerification.id;
  const verification = usingSubmittedVerification ? submittedVerification : serverVerification;
  const verificationStatus = verification?.status ?? "unverified";
  const verificationStateLoading = verificationLoading && !usingSubmittedVerification;
  const verificationStateError = verificationQueryError && !usingSubmittedVerification;
  const submittedVerificationId = submittedVerification?.id;

  useEffect(() => {
    if (!me?.payout_data) return;

    const nextForm = payoutDataToForm(me.payout_data);
    setSavedForm(nextForm);
    setForm(nextForm);
    setIsEditing(false);
  }, [me?.payout_data]);

  useEffect(() => {
    if (!savedForm && me?.name && !nameTouched && !form.name) {
      setForm((prev) => ({ ...prev, name: me.name ?? "" }));
    }
  }, [me, nameTouched, form.name, savedForm]);

  useEffect(() => {
    if (!submittedVerificationId || serverVerification?.id !== submittedVerificationId) return;

    setSubmittedVerification(null);
    setVerificationSubmitError((current) =>
      current === VERIFICATION_REFRESH_WARNING ? null : current,
    );
  }, [serverVerification?.id, submittedVerificationId]);

  // Redirect if no balance once data is available (disabled during dev/review)
  // useEffect(() => {
  //   if (!loading && (!wallet || wallet.available_cents <= 0)) {
  //     navigate("/earn", { replace: true });
  //   }
  // }, [loading, wallet, navigate]);

  const meetsMinimum = (wallet?.available_cents ?? 0) >= MIN_PAYOUT_CENTS;
  const canRedeemForPlan = (wallet?.available_cents ?? 0) > 0 && !hasPendingPayout;
  const formLocked = isSaved && !isEditing;
  const documentValid = isValidCpfOrCnpj(form.cpf);
  const accountHasDigit = hasAccountVerifierDigit(form.account);
  const hasPixKey = form.pixKey.trim() !== "";
  const bankFieldsStarted =
    form.bank.trim() !== "" || form.agency.trim() !== "" || form.account.trim() !== "";
  const bankDetailsComplete =
    form.bank.trim() !== "" &&
    form.agency.trim() !== "" &&
    form.account.trim() !== "" &&
    accountHasDigit;
  const showAccountDigitError = form.account.trim() !== "" && !accountHasDigit && !formLocked;
  const formValid =
    form.name.trim() !== "" &&
    form.cpf.trim() !== "" &&
    documentValid &&
    (bankDetailsComplete || (hasPixKey && !bankFieldsStarted));
  const paymentDataReady = isSaved && !isEditing;
  const verificationMatchesDocument =
    documentValid &&
    normalizeBrazilianDocument(verification?.cpf) === normalizeBrazilianDocument(form.cpf);
  const verificationNeedsCurrentDocument =
    verificationStatus === "verified" && documentValid && !verificationMatchesDocument;
  const pixVerified =
    verificationStatus === "verified" &&
    verificationMatchesDocument &&
    !verificationLoading &&
    !verificationQueryError;
  const canRequest = paymentDataReady && meetsMinimum && pixVerified;
  const showDocumentError = form.cpf.trim() !== "" && !documentValid && !formLocked;
  const verificationNeedsDocument =
    verificationStatus === "unverified" ||
    verificationStatus === "rejected" ||
    verificationNeedsCurrentDocument;
  const verificationBusy = uploadingDocument || uploadingDocuments || submittingVerification;
  const canSubmitVerification = !!documentFiles.front && documentValid && !verificationBusy;
  const mobileActions = resolveWalletMobileActions({
    canRedeemForPlan,
    canRequestPayout: canRequest && !hasPendingPayout,
    verificationReadyToSend:
      !verificationStateLoading &&
      !verificationStateError &&
      verificationNeedsDocument &&
      !!documentFiles.front,
  });

  const setField = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (field === "name") setNameTouched(true);
    const value = field === "account" ? formatBankAccount(e.target.value) : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formValid || savingPayoutData) return;

    setSaveError(null);

    try {
      const { data } = await updateMyPayoutData({
        variables: {
          input: {
            name: form.name,
            document: form.cpf,
            bank: form.bank,
            agency: form.agency,
            account: form.account,
            pixKey: form.pixKey,
          },
        },
      });

      const nextForm = data?.updateMyPayoutData
        ? payoutDataToForm(data.updateMyPayoutData)
        : { ...form };

      setSavedForm(nextForm);
      setForm(nextForm);
      setIsEditing(false);
    } catch {
      setSaveError("Não foi possível salvar os dados. Tente novamente.");
    }
  };

  const handleEdit = () => {
    setSaveError(null);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    if (savedForm) setForm(savedForm);
    setSaveError(null);
    setIsEditing(false);
  };

  const handleDocumentChange =
    (side: DocumentSide) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) {
        setDocumentFiles((prev) => ({ ...prev, [side]: null }));
        return;
      }
      if (!ACCEPTED_DOCUMENT_TYPES.includes(file.type)) {
        setDocumentFiles((prev) => ({ ...prev, [side]: null }));
        setDocumentError("Envie uma foto (JPG/PNG) ou um PDF do documento.");
        e.target.value = "";
        return;
      }
      setDocumentError(null);
      setVerificationSubmitError(null);
      setDocumentFiles((prev) => ({ ...prev, [side]: file }));
    };

  const handleSubmitVerification = async () => {
    const { front, back } = documentFiles;
    if (!canSubmitVerification || !front) return;
    const selectedCpf = form.cpf;
    setVerificationSubmitError(null);
    setUploadingDocuments(true);
    const [documentKey, documentBackKey] = await Promise.all([
      uploadDocument(front),
      back ? uploadDocument(back) : Promise.resolve(null),
    ]);
    setUploadingDocuments(false);
    if (!documentKey || (back && !documentBackKey)) {
      setVerificationSubmitError("Não foi possível enviar o documento. Tente novamente.");
      return;
    }
    try {
      const { data } = await submitVerification({
        variables: {
          cpf: selectedCpf,
          document_url: documentKey,
          ...(documentBackKey ? { document_back_url: documentBackKey } : {}),
        },
      });

      if (!data?.submitMyPixVerification) {
        setVerificationSubmitError("Não foi possível enviar a verificação. Tente novamente.");
        return;
      }

      setSubmittedVerification(data.submitMyPixVerification);
      setDocumentFiles(NO_DOCUMENT_FILES);
    } catch (error) {
      setVerificationSubmitError(pixVerificationErrorMessage(error));
      return;
    }

    try {
      await refetchVerification();
    } catch {
      setVerificationSubmitError(VERIFICATION_REFRESH_WARNING);
    }
  };

  const handleConfirmInApp = async () => {
    if (!canRequest || hasPendingPayout || !wallet || !me || requestingPayout) return;
    try {
      await requestCpmPayout({
        variables: {
          input: buildPayoutRequestInput({
            wallet,
            form: {
              cpf: form.cpf,
              pixKey: form.pixKey,
              bank: form.bank,
              agency: form.agency,
              account: form.account,
            },
          }),
        },
      });
      setStatementRefresh((key) => key + 1);
      setShowModal(false);
    } catch (err) {
      setShowModal(false);
      const alreadyPending =
        err instanceof Error && err.message.includes("payout_request_already_pending");
      setRequestError(
        alreadyPending
          ? "Você já possui uma solicitação de saque em análise."
          : "Não foi possível enviar a solicitação. Tente novamente."
      );
    }
  };

  const handleRequest = () => {
    if (!canRequest || !wallet || !me) return;
    const phone = resolveSupportWhatsapp(import.meta.env.VITE_SUPPORT_WHATSAPP);
    const url = buildWhatsappPayoutUrl({
      phone,
      email: me.email ?? "",
      name: form.name,
      breakdown: wallet.per_campaign_breakdown.map((e) => ({
        campaign_name: e.campaign_name,
        earned_cents: e.earned_cents,
      })),
      totalCents: wallet.available_cents,
      paidOutCents: wallet.total_paid_out_cents,
      bankData: {
        cpf: form.cpf,
        bank: form.bank,
        agency: form.agency,
        account: form.account,
        pixKey: form.pixKey,
      },
    });
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Layout>
      <main
        className={cn(
          "flex-1 p-4 sm:p-6 lg:p-8 lg:pb-8 overflow-y-auto",
          // Room for the fixed mobile bar, so it never covers the last block.
          mobileActions.length > 1 ? "pb-40" : "pb-24",
        )}
      >
        <div className="space-y-6">

          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
              <Wallet className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Carteira</h1>
              <p className="text-muted-foreground text-sm">
                Acompanhe seus ganhos e solicite seu saque
              </p>
            </div>
          </div>

          {/* Two-column layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">

            {/* Left column: balance cards + breakdown */}
            <div className="space-y-4">

              {loading ? (
                <div className="space-y-3">
                  <Skeleton className="h-36 rounded-xl" />
                  <div className="grid grid-cols-2 gap-3">
                    <Skeleton className="h-24 rounded-xl" />
                    <Skeleton className="h-24 rounded-xl" />
                  </div>
                </div>
              ) : wallet && (
                <div className="space-y-3">
                  {/* Hero "Disponível" card */}
                  <Card className="relative overflow-hidden border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-card">
                    <img
                      src="/clip-pix-logo-white.png"
                      alt=""
                      aria-hidden="true"
                      className="absolute top-4 right-4 w-24 h-24 object-contain opacity-[0.06] pointer-events-none select-none"
                    />
                    <CardContent className="p-5">
                      <p className={cn("text-xs font-semibold uppercase tracking-wider mb-2", hasPendingPayout ? "text-amber-400" : "text-emerald-400")}>
                        Disponível
                      </p>
                      <div className="flex items-end gap-3 flex-wrap">
                        <p className={cn("text-4xl font-bold", hasPendingPayout && "text-muted-foreground", wallet.available_cents < 0 && "text-red-400")}>
                          {formatCents(wallet.available_cents, wallet.currency ?? "BRL")}
                        </p>
                        {hasPendingPayout ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-400 text-xs font-bold mb-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                            Em análise
                          </span>
                        ) : hasAffiliateHold ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-400 text-xs font-bold mb-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                            {t('wallet.affiliateHoldBadge')}
                          </span>
                        ) : meetsMinimum ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-bold mb-1">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                            Pronto para saque
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground mb-1">
                            Mínimo: {formatCents(MIN_PAYOUT_CENTS, "BRL")}
                          </span>
                        )}
                      </div>
                      {!hasPendingPayout && wallet.available_cents > 0 && (
                        <p className="text-xs text-muted-foreground mt-1.5">
                          Taxa de saque: {PAYOUT_FEE_PERCENT}% — valor a receber:{" "}
                          <span className="font-semibold text-foreground">
                            {formatCents(calcPayoutNet(wallet.available_cents), wallet.currency ?? "BRL")}
                          </span>
                        </p>
                      )}
                      {hasPendingPayout && (
                        <div className="mt-4 flex items-center gap-2.5 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                          <Clock className="w-4 h-4 text-amber-400 shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-amber-300">Solicitação enviada</p>
                            <p className="text-xs text-amber-400/70">Aguardando aprovação — até 3 dias úteis</p>
                          </div>
                        </div>
                      )}

                      {canRedeemForPlan && (
                        <div className="mt-4">
                          <Button
                            onClick={() => navigate("/plans")}
                            className="w-full font-semibold gap-2 bg-lime-400 hover:bg-lime-500 text-lime-950"
                          >
                            <Sparkles className="w-4 h-4" />
                            Assinar plano com o saldo
                          </Button>
                          <p className="text-xs font-medium text-lime-600 dark:text-lime-400 text-center mt-1.5">
                            Sem taxa pra assinar. Sem limite pra cortar. Sem marca d'água pra travar seu alcance.
                          </p>
                        </div>
                      )}

                      {meetsMinimum && !hasPendingPayout && (
                        <div className={cn("space-y-2", canRedeemForPlan ? "mt-3" : "mt-4")}>
                          <>
                              <Button
                                onClick={() => setShowModal(true)}
                                disabled={!canRequest}
                                variant={canRedeemForPlan ? "outline" : "default"}
                                className={cn(
                                  "w-full font-semibold gap-2 transition-colors",
                                  !canRedeemForPlan && (
                                    canRequest
                                      ? "bg-emerald-400 hover:bg-emerald-500 text-emerald-950"
                                      : "bg-muted text-muted-foreground disabled:opacity-60"
                                  )
                                )}
                              >
                                <Send className="w-4 h-4" />
                                Solicitar Saque
                              </Button>
                              {canRequest && (
                                <button
                                  onClick={handleRequest}
                                  className="w-full text-xs text-muted-foreground hover:text-foreground text-center py-1 transition-colors hover:underline underline-offset-2"
                                >
                                  ou solicitar via WhatsApp
                                  <ExternalLink className="inline w-3 h-3 ml-1" />
                                </button>
                              )}
                              {!canRequest && (
                                <p className="text-xs text-muted-foreground text-center">
                                  {!paymentDataReady
                                    ? "Preencha e salve sua chave PIX ou dados bancários para habilitar o saque."
                                    : "Verifique seu PIX (CPF + documento) ao lado para habilitar o saque."}
                                </p>
                              )}
                            </>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <MonthlyEarningsChart />

                  {/* Secondary stats */}
                  <div className={cn(
                    "grid gap-3",
                    hasPendingPayout && hasAffiliateHold
                      ? "grid-cols-1 sm:grid-cols-2"
                      : hasPendingPayout || hasAffiliateHold
                        ? "grid-cols-1 sm:grid-cols-3"
                        : "grid-cols-2",
                  )}>
                    <Card>
                      <CardContent className="p-4">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                          Total ganho
                        </p>
                        <p className="text-xl font-bold">
                          {formatCents(wallet.total_earned_cents, wallet.currency ?? "BRL")}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {t('wallet.totalEarnedDescription')}
                        </p>
                        {!!wallet.affiliate_earned_cents && wallet.affiliate_earned_cents > 0 && (
                          <p className="text-xs text-muted-foreground">
                            {t('affiliates.wallet.affiliateBreakdown', {
                              amount: formatCents(wallet.affiliate_earned_cents, wallet.currency ?? "BRL"),
                            })}
                          </p>
                        )}
                        {wallet.producer_earned_cents > 0 && (
                          <div className="mt-2 space-y-2 border-t border-border/50 pt-2">
                            <p className="text-xs text-muted-foreground">
                              {t('wallet.producerBreakdown', {
                                amount: formatCents(
                                  wallet.producer_earned_cents,
                                  wallet.currency ?? "BRL",
                                ),
                              })}
                            </p>
                            <Button
                              asChild
                              variant="outline"
                              size="sm"
                              className="h-8 w-full justify-between text-xs"
                            >
                              <Link to="/course-royalties">
                                {t('wallet.viewCourseRoyalties')}
                                <ExternalLink className="h-3.5 w-3.5" />
                              </Link>
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                    {hasPendingPayout && isRedemptionHold && (
                      <Card className="border-amber-500/30 bg-amber-500/5">
                        <CardContent className="p-4">
                          <p className="text-xs font-semibold text-amber-400 uppercase tracking-wider mb-1.5">
                            Resgate com saldo
                          </p>
                          <p className="text-xl font-bold text-amber-300">
                            {formatCents(pendingPayoutCents, wallet.currency ?? "BRL")}
                          </p>
                          <p className="text-xs text-amber-400/70 mt-1.5">
                            {t('wallet.pendingHoldNotice')}
                          </p>
                          <CancelPendingHoldButton
                            className="mt-2.5 w-full"
                            onCancelled={async () => {
                              setStatementRefresh((key) => key + 1);
                              await refetchWallet();
                            }}
                          />
                        </CardContent>
                      </Card>
                    )}
                    {hasPendingPayout && !isRedemptionHold && (
                      <Card className="border-amber-500/30 bg-amber-500/5">
                        <CardContent className="p-4">
                          <p className="text-xs font-semibold text-amber-400 uppercase tracking-wider mb-1.5">
                            Em análise
                          </p>
                          <p className="text-xl font-bold text-amber-300">
                            {formatCents(pendingPayoutCents, wallet.currency ?? "BRL")}
                          </p>
                          <p className="text-xs text-amber-400/70 mt-0.5">Aguardando aprovação</p>
                          <p className="text-xs text-amber-400/70 mt-1.5 pt-1.5 border-t border-amber-500/20">
                            Taxa de {PAYOUT_FEE_PERCENT}%: − {formatCents(calcPayoutFee(pendingPayoutCents), wallet.currency ?? "BRL")}
                            <br />
                            Você vai receber:{" "}
                            <span className="font-semibold text-amber-300">
                              {formatCents(calcPayoutNet(pendingPayoutCents), wallet.currency ?? "BRL")}
                            </span>
                          </p>
                        </CardContent>
                      </Card>
                    )}
                    {hasAffiliateHold && (
                      <Card className="border-amber-500/30 bg-amber-500/5">
                        <CardContent className="p-4">
                          <p className="text-xs font-semibold text-amber-400 uppercase tracking-wider mb-1.5">
                            {t('wallet.affiliateHoldTitle')}
                          </p>
                          <p className="text-xl font-bold text-amber-300">
                            {formatCents(affiliatePendingCents, wallet.currency ?? "BRL")}
                          </p>
                          <p className="text-xs text-amber-400/70 mt-0.5">
                            {t('wallet.affiliateHoldProtection')}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1.5">
                            {t('wallet.affiliateHoldDescription')}
                          </p>
                        </CardContent>
                      </Card>
                    )}
                    <Card>
                      <CardContent className="p-4">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                          Já sacado
                        </p>
                        <p className="text-xl font-bold">
                          {formatCents(wallet.total_paid_out_cents, wallet.currency ?? "BRL")}
                        </p>
                        <a href="#extrato" className="mt-0.5 inline-block text-xs text-primary hover:underline">
                          {t("walletStatement.withdrawHistoryLink")}
                        </a>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              )}

              <WalletStatement id="extrato" title={t("walletStatement.title")} refreshKey={statementRefresh} />

              {/* Breakdown por campanha */}
              {!loading && (
                <Card>
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <Scissors className="w-4 h-4 text-muted-foreground" />
                      <CardTitle className="text-base">Ganhos por campanha</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-0">
                    {wallet && wallet.per_campaign_breakdown.filter((e) => e.earned_cents > 0).length > 0 ? (
                      <>
                        {wallet.per_campaign_breakdown
                          .filter((e) => e.earned_cents > 0)
                          .map((e) => (
                            <div
                              key={e.campaign_id}
                              className="flex items-center gap-3 py-2.5 border-b border-border/50 last:border-0"
                            >
                              <span className="min-w-0 flex-1 break-words text-sm text-muted-foreground">{e.campaign_name}</span>
                              <span className="shrink-0 text-sm font-semibold">
                                {formatCents(e.earned_cents, wallet.currency ?? "BRL")}
                              </span>
                              <Button
                                asChild
                                variant="outline"
                                size="sm"
                                className={cn("h-7 shrink-0 gap-1 px-2 text-xs", CAMPAIGN_LINK_WIDTH)}
                              >
                                <Link
                                  to={`/campaigns/${e.campaign_id}`}
                                  aria-label={t('wallet.viewCampaignAria', { name: e.campaign_name })}
                                >
                                  <span className="hidden sm:inline">{t('wallet.viewCampaign')}</span>
                                  <ChevronRight className="h-3.5 w-3.5" />
                                </Link>
                              </Button>
                            </div>
                          ))}
                        <div className="flex items-center gap-3 pt-3 mt-1">
                          <span className="flex-1 text-sm font-semibold">Total a receber</span>
                          <span className={cn("text-base font-bold", wallet.available_cents < 0 ? "text-red-400" : "text-emerald-400")}>
                            {formatCents(wallet.available_cents, wallet.currency ?? "BRL")}
                          </span>
                          {/* Keeps the total under the per-campaign amounts, not under the buttons. */}
                          <span aria-hidden className={cn("shrink-0", CAMPAIGN_LINK_WIDTH)} />
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground py-2">
                        Nenhum ganho registrado ainda. Participe de campanhas para acumular saldo.
                      </p>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Right column: banking form (sticky on large screens) */}
            <div className="lg:sticky lg:top-6 space-y-4">
              <Card>
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-muted-foreground" />
                      <CardTitle className="text-base">Dados para saque</CardTitle>
                      {isSaved && !isEditing && (
                        <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                      )}
                    </div>
                    {isSaved && !isEditing && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleEdit}
                        className="h-7 px-2 gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                      >
                        <Pencil className="w-3 h-3" />
                        Editar
                      </Button>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {isSaved && !isEditing
                      ? "Dados salvos. Edite somente se precisar atualizar."
                      : "Preencha e salve uma chave PIX ou dados bancários completos."}
                  </p>
                </CardHeader>

                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="wallet-name">Nome completo</Label>
                    <Input
                      id="wallet-name"
                      placeholder="Como aparece no seu documento"
                      value={form.name}
                      onChange={setField("name")}
                      disabled={formLocked}
                      className={cn(formLocked && "opacity-60 cursor-default")}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="wallet-cpf">CPF / CNPJ</Label>
                    <Input
                      id="wallet-cpf"
                      placeholder="000.000.000-00"
                      value={form.cpf}
                      onChange={setField("cpf")}
                      disabled={formLocked}
                      className={cn(formLocked && "opacity-60 cursor-default")}
                    />
                    {showDocumentError && (
                      <p className="text-xs text-destructive">
                        Informe um CPF ou CNPJ válido.
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="wallet-pix-key">Chave PIX</Label>
                    <Input
                      id="wallet-pix-key"
                      placeholder="CPF, CNPJ, e-mail, telefone ou chave aleatória"
                      value={form.pixKey}
                      onChange={setField("pixKey")}
                      disabled={formLocked}
                      className={cn(formLocked && "opacity-60 cursor-default")}
                    />
                    <p className="text-xs text-muted-foreground">
                      Ao informar uma chave PIX, os dados bancários abaixo são opcionais.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="wallet-bank">Banco</Label>
                    <Input
                      id="wallet-bank"
                      placeholder="Ex: Nubank, Itaú, Bradesco..."
                      value={form.bank}
                      onChange={setField("bank")}
                      disabled={formLocked}
                      className={cn(formLocked && "opacity-60 cursor-default")}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="wallet-agency">Agência</Label>
                      <Input
                        id="wallet-agency"
                        placeholder="0000"
                        value={form.agency}
                        onChange={setField("agency")}
                        disabled={formLocked}
                        className={cn(formLocked && "opacity-60 cursor-default")}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="wallet-account">Conta</Label>
                      <Input
                        id="wallet-account"
                        placeholder="00000-0"
                        value={form.account}
                        onChange={setField("account")}
                        disabled={formLocked}
                        className={cn(formLocked && "opacity-60 cursor-default")}
                      />
                      {showAccountDigitError && (
                        <p className="text-xs text-destructive">
                          Inclua o dígito da conta, ex: 12345-6.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Save / Cancel buttons (shown when form is editable) */}
                  {(!isSaved || isEditing) && (
                    <div className="flex gap-2">
                      {isEditing && (
                        <Button
                          variant="outline"
                          onClick={handleCancelEdit}
                          className="flex-1"
                        >
                          Cancelar
                        </Button>
                      )}
                      <Button
                        onClick={handleSave}
                        disabled={!formValid || savingPayoutData}
                        className="flex-1 font-semibold disabled:opacity-50"
                      >
                        {savingPayoutData ? "Salvando..." : "Salvar dados"}
                      </Button>
                    </div>
                  )}

                  {saveError && (
                    <p className="text-xs text-destructive text-center">
                      {saveError}
                    </p>
                  )}

                  {canRequest && !hasPendingPayout && (
                    <p className="text-xs text-muted-foreground text-center">
                      Sua solicitação será registrada e processada em até 3 dias úteis.
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* PIX verification */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-muted-foreground" />
                    <CardTitle className="text-base">Verificação de PIX</CardTitle>
                    {pixVerified && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Necessário para liberar o saque — evita fraude e recebimento em contas de terceiros.
                  </p>
                </CardHeader>
                <CardContent className="space-y-3">
                  {verificationStateLoading ? (
                    <p className="text-sm text-muted-foreground">Carregando verificação...</p>
                  ) : verificationStateError ? (
                    <div className="space-y-2" role="alert">
                      <p className="text-sm text-destructive">
                        Não foi possível carregar a verificação de PIX.
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => void refetchVerification()}
                      >
                        Tentar novamente
                      </Button>
                    </div>
                  ) : (
                    <>
                      {verificationStatus === "verified" && !verificationNeedsCurrentDocument && (
                        <div className="flex items-center gap-2.5 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
                          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-emerald-300">PIX verificado</p>
                            <p className="text-xs text-emerald-400/70">Pronto para saque</p>
                          </div>
                        </div>
                      )}

                      {verificationNeedsCurrentDocument && (
                        <div className="flex items-center gap-2.5 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                          <Clock className="w-4 h-4 text-amber-400 shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-amber-300">
                              Verificação necessária
                            </p>
                            <p className="text-xs text-amber-400/70">
                              Verifique o CPF/CNPJ salvo para liberar o saque.
                            </p>
                          </div>
                        </div>
                      )}

                      {verificationStatus === "pending" && (
                        <div className="flex items-center gap-2.5 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                          <Clock className="w-4 h-4 text-amber-400 shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-amber-300">Em análise</p>
                            <p className="text-xs text-amber-400/70">Normalmente até 48h</p>
                          </div>
                        </div>
                      )}

                      {verificationStatus === "rejected" && (
                        <div className="flex items-center gap-2.5 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                          <XCircle className="w-4 h-4 text-red-400 shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-red-300">Documento reprovado</p>
                            <p className="text-xs text-red-400/70">
                              {verification?.review_reason || "Reenvie o comprovante."}
                            </p>
                          </div>
                        </div>
                      )}

                      {verificationSubmitError && (
                        <p className="text-xs text-destructive" role="alert">
                          {verificationSubmitError}
                        </p>
                      )}

                      {verificationNeedsDocument && (
                        <>
                          <div className="space-y-2">
                            <p className="text-sm font-medium">Comprovante do documento (RG/CNH)</p>
                            <p className="text-xs text-muted-foreground">
                              Mande a frente e o verso em fotos separadas, ou um PDF com o documento inteiro.
                            </p>
                            <div className="grid grid-cols-2 gap-2">
                              <DocumentSlot
                                id="wallet-document-front"
                                label="Frente"
                                hint="Foto (JPG/PNG) ou PDF"
                                file={documentFiles.front}
                                disabled={verificationBusy}
                                onChange={handleDocumentChange("front")}
                              />
                              <DocumentSlot
                                id="wallet-document-back"
                                label="Verso (opcional)"
                                hint="Foto do verso, se tiver"
                                file={documentFiles.back}
                                disabled={verificationBusy}
                                onChange={handleDocumentChange("back")}
                              />
                            </div>
                            {documentError && (
                              <p className="text-xs text-destructive" role="alert">
                                {documentError}
                              </p>
                            )}
                          </div>

                          {!documentValid && form.cpf.trim() !== "" && (
                            <p className="text-xs text-destructive" role="alert">
                              Informe um CPF válido no campo acima antes de enviar.
                            </p>
                          )}

                          <p className="text-xs text-muted-foreground">
                            Isso não bloqueia seu cadastro — só o recebimento de saldo das campanhas até ser aprovado.
                          </p>

                          <Button
                            onClick={handleSubmitVerification}
                            disabled={!canSubmitVerification}
                            variant="outline"
                            className="w-full"
                          >
                            {uploadingDocument || uploadingDocuments
                              ? "Enviando documento..."
                              : submittingVerification
                                ? "Enviando para análise..."
                                : "Enviar para verificação"}
                          </Button>
                        </>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

          </div>
        </div>
      </main>

      {/* Mobile sticky CTA */}
      {mobileActions.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 lg:hidden z-40 p-4 bg-background/95 backdrop-blur-sm border-t border-border space-y-2">
          {mobileActions.includes("send_verification") && (
            <Button
              onClick={handleSubmitVerification}
              disabled={!canSubmitVerification}
              className="w-full h-12 bg-emerald-400 hover:bg-emerald-500 text-emerald-950 font-semibold gap-2 disabled:opacity-60"
            >
              {verificationBusy ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {verificationBusy ? "Enviando..." : "Enviar documento"}
            </Button>
          )}
          {mobileActions.includes("redeem_plan") && (
            <Button
              onClick={() => navigate("/plans")}
              className="w-full h-12 bg-lime-400 hover:bg-lime-500 text-lime-950 font-semibold gap-2"
            >
              <Sparkles className="w-4 h-4" />
              Assinar plano com o saldo
            </Button>
          )}
          {mobileActions.includes("request_payout") && (
            <Button
              onClick={() => setShowModal(true)}
              variant={mobileActions.includes("redeem_plan") ? "outline" : "default"}
              className={cn(
                "w-full h-12 font-semibold gap-2",
                !mobileActions.includes("redeem_plan") && "bg-emerald-400 hover:bg-emerald-500 text-emerald-950"
              )}
            >
              <Send className="w-4 h-4" />
              Solicitar Saque
            </Button>
          )}
        </div>
      )}

      {/* Confirmation modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar solicitação de saque</DialogTitle>
          </DialogHeader>
          {wallet && (() => {
            const feeAmountCents = calcPayoutFee(wallet.available_cents);
            const netAmountCents = calcPayoutNet(wallet.available_cents);
            const currency = wallet.currency ?? "BRL";
            return (
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-center">
                  <p className="text-xs text-emerald-400 uppercase font-semibold tracking-wider mb-1">Valor a receber</p>
                  <p className="text-3xl font-bold">{formatCents(netAmountCents, currency)}</p>
                  <div className="mt-2 space-y-0.5 text-xs text-muted-foreground">
                    <div className="flex justify-between px-2">
                      <span>Valor bruto</span>
                      <span>{formatCents(wallet.available_cents, currency)}</span>
                    </div>
                    <div className="flex justify-between px-2 text-amber-400/80">
                      <span>Taxa de saque ({PAYOUT_FEE_PERCENT}%)</span>
                      <span>− {formatCents(feeAmountCents, currency)}</span>
                    </div>
                  </div>
                </div>
                {wallet.per_campaign_breakdown.filter((e) => e.earned_cents > 0).length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Composição</p>
                    {wallet.per_campaign_breakdown.filter((e) => e.earned_cents > 0).map((e) => (
                      <div key={e.campaign_id} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">{e.campaign_name}</span>
                        <span className="font-medium">{formatCents(e.earned_cents, currency)}</span>
                      </div>
                    ))}
                  </div>
                )}
                <div className="space-y-1 pt-1 border-t border-border">
                  <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Dados de recebimento</p>
                  <p className="text-sm font-medium">{form.name} — {form.cpf}</p>
                  {form.pixKey && <p className="text-sm text-muted-foreground">PIX: {form.pixKey}</p>}
                  {!form.pixKey && form.bank && (
                    <p className="text-sm text-muted-foreground">{form.bank} • Ag {form.agency} • Cc {form.account}</p>
                  )}
                </div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowModal(false)}>Cancelar</Button>
            <Button
              onClick={handleConfirmInApp}
              disabled={!canRequest || hasPendingPayout || requestingPayout}
              className="bg-emerald-400 hover:bg-emerald-500 text-emerald-950 font-semibold gap-2 disabled:opacity-60"
            >
              <Send className="w-4 h-4" />
              {requestingPayout ? "Enviando..." : "Confirmar solicitação"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request error dialog */}
      <Dialog open={!!requestError} onOpenChange={() => setRequestError(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Erro ao solicitar saque</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">{requestError}</p>
          <DialogFooter>
            <Button onClick={() => setRequestError(null)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default EarnWallet;
